import { createSlice } from '@reduxjs/toolkit';

// Session timeout: 20 minutes
const SESSION_TIMEOUT = 20 * 60 * 1000; // 20 minutes in milliseconds

// Load initial state from localStorage
const loadAuthFromStorage = () => {
  try {
    const authData = localStorage.getItem('yk_auth');
    if (authData) {
      const parsed = JSON.parse(authData);
      // Check if session is still valid (20 minutes)
      const now = new Date().getTime();
      if (parsed.expiresAt && now < parsed.expiresAt) {
        return {
          isAuthenticated: true,
          user: parsed.user,
          entity: parsed.entity,
          loginTime: parsed.loginTime,
          expiresAt: parsed.expiresAt,
          lastActivity: parsed.lastActivity || now,
        };
      } else {
        localStorage.removeItem('yk_auth');
      }
    }
  } catch (error) {
    console.error('Error loading auth from storage:', error);
    localStorage.removeItem('yk_auth');
  }
  return {
    isAuthenticated: false,
    user: null,
    entity: 'RMS',
    loginTime: null,
    expiresAt: null,
    lastActivity: null,
  };
};

const initialState = loadAuthFromStorage();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Login action
    login: (state, action) => {
      const { email, entity, userData } = action.payload;
      const now = new Date().getTime();
      const expiresAt = now + SESSION_TIMEOUT; // 20 minutes

      state.isAuthenticated = true;
      state.user = {
        email,
        ...userData,
      };
      state.entity = entity || 'RMS';
      state.loginTime = now;
      state.expiresAt = expiresAt;
      state.lastActivity = now;

      // Persist to localStorage
      try {
        localStorage.setItem('yk_auth', JSON.stringify({
          user: state.user,
          entity: state.entity,
          loginTime: state.loginTime,
          expiresAt: state.expiresAt,
          lastActivity: state.lastActivity,
        }));
      } catch (error) {
        console.error('Error saving auth to storage:', error);
      }
    },

    // Logout action
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.entity = 'RMS';
      state.loginTime = null;
      state.expiresAt = null;
      state.lastActivity = null;

      // Clear from localStorage
      try {
        localStorage.removeItem('yk_auth');
      } catch (error) {
        console.error('Error removing auth from storage:', error);
      }
    },

    // Check and refresh session
    checkSession: (state) => {
      const now = new Date().getTime();
      if (state.expiresAt && now >= state.expiresAt) {
        // Session expired
        state.isAuthenticated = false;
        state.user = null;
        state.entity = 'RMS';
        state.loginTime = null;
        state.expiresAt = null;
        state.lastActivity = null;
        try {
          localStorage.removeItem('yk_auth');
        } catch (error) {
          console.error('Error removing expired auth:', error);
        }
      }
    },

    // Update last activity and extend session
    updateActivity: (state) => {
      const now = new Date().getTime();
      state.lastActivity = now;
      state.expiresAt = now + SESSION_TIMEOUT; // Reset to 20 minutes from now

      // Update localStorage
      try {
        const authData = localStorage.getItem('yk_auth');
        if (authData) {
          const parsed = JSON.parse(authData);
          parsed.lastActivity = now;
          parsed.expiresAt = state.expiresAt;
          localStorage.setItem('yk_auth', JSON.stringify(parsed));
        }
      } catch (error) {
        console.error('Error updating activity in storage:', error);
      }
    },

    // Extend session by 20 more minutes
    extendSession: (state) => {
      const now = new Date().getTime();
      state.expiresAt = now + SESSION_TIMEOUT; // Add 20 more minutes
      state.lastActivity = now;

      // Update localStorage
      try {
        const authData = localStorage.getItem('yk_auth');
        if (authData) {
          const parsed = JSON.parse(authData);
          parsed.expiresAt = state.expiresAt;
          parsed.lastActivity = state.lastActivity;
          localStorage.setItem('yk_auth', JSON.stringify(parsed));
        }
      } catch (error) {
        console.error('Error extending session:', error);
      }
    },
  },
});

export const { login, logout, checkSession, extendSession, updateActivity } = authSlice.actions;
export default authSlice.reducer;
